import { toast } from 'sonner';

import { useRunAffiliation } from '@/hooks/affiliation';
import { useAxios } from '@/hooks/useAxios';
import { useBulkCapabilityJob } from '@/hooks/useCapability';
import { CAPABILITIES } from '@/sections/asset/constants';
import { ScanOptions, ScanType } from '@/sections/asset/types';
import {
  buildComprehensiveScanJobs,
  buildCrawlJobs,
  buildSimpleCapabilityJobs,
  buildWebScanJobs,
} from '@/sections/asset/utils/scanning';

/**
 * Custom hook for asset scan handlers
 * Orchestrates scan execution by delegating to utility functions
 *
 * @returns Object containing handleScan function
 */
export const useAssetScanHandlers = () => {
  const axios = useAxios();
  const { mutateAsync: runJobs } = useBulkCapabilityJob();
  const { mutateAsync: runAffiliation } = useRunAffiliation();

  /**
   * Executes a scan on the provided assets
   *
   * @param assets - Assets to scan
   * @param type - Type of scan to execute
   * @param options - Optional scan configuration
   */
  const handleScan = async (
    assets: { key: string }[],
    type: ScanType,
    options?: ScanOptions
  ) => {
    // Validate asset keys before processing
    const invalidAssets = assets.filter(
      asset =>
        !asset.key ||
        (!asset.key.startsWith('#asset#') &&
          !asset.key.startsWith('#webapplication#') &&
          !asset.key.startsWith('#port#'))
    );

    if (invalidAssets.length > 0) {
      console.error('[useAssetScanHandlers] Invalid asset keys detected:', {
        invalidAssets,
        allAssets: assets,
      });
      toast.error(
        `Invalid asset data detected. ${invalidAssets.length} of ${assets.length} assets have malformed keys.`
      );
      return;
    }

    try {
      switch (type) {
        case 'affiliation': {
          // Affiliation uses special API, not capability jobs
          await Promise.all(assets.map(asset => runAffiliation(asset.key)));
          break;
        }

        case 'port': {
          // Simple port scan capability
          const jobs = buildSimpleCapabilityJobs(
            assets,
            CAPABILITIES.PORT_SCAN
          );
          await runJobs(jobs);
          break;
        }

        case 'crawl': {
          // Complex web crawl with multiple resolution paths
          const jobs = await buildCrawlJobs(assets, axios, options);

          if (jobs.length === 0) {
            toast.error('No web applications found to crawl');
            return;
          }

          await runJobs(jobs);
          break;
        }

        case 'webscan': {
          // Complex web scan with multiple resolution paths
          const jobs = await buildWebScanJobs(assets, axios);

          if (jobs.length === 0) {
            toast.error('No web applications or HTTP endpoints found to scan');
            return;
          }

          await runJobs(jobs);
          break;
        }

        case 'network': {
          // Simple network scan capability
          const jobs = buildSimpleCapabilityJobs(assets, CAPABILITIES.NUCLEI);
          await runJobs(jobs);
          break;
        }

        case 'comprehensive': {
          // Comprehensive scan runs multiple capabilities
          const jobs = await buildComprehensiveScanJobs(assets, axios, options);
          await runJobs(jobs);
          break;
        }

        default: {
          // Type guard - should never happen with proper TypeScript
          const exhaustiveCheck: never = type;
          throw new Error(`Unhandled scan type: ${exhaustiveCheck}`);
        }
      }
    } catch (error) {
      console.error('Failed to execute scan:', error);
      toast.error('Failed to execute scan');
    }
  };

  return { handleScan };
};
